import { formatDate, formatPhone, fullName } from '@hr/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeftIcon, KeyRoundIcon, UserRoundXIcon } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { EmptyState } from '@/components/page';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MailboxPanel } from '@/features/mail/mailbox-panel';
import { Correspondence, EmailHistory, SendEmailButton } from '@/features/mail/send-email';
import { AssignmentsTab } from '@/features/personnel/assignments-tab';
import { ContractsTab } from '@/features/personnel/contracts-tab';
import { DocumentsTab } from '@/features/personnel/documents-tab';
import { AccountDialog, DismissDialog } from '@/features/personnel/employee-dialogs';
import { EmployeeForm } from '@/features/personnel/employee-form';
import { OnboardingTab } from '@/features/personnel/onboarding-tab';
import { EmployeeStatusBadge } from '@/features/personnel/status';
import { uk } from '@/i18n/uk';
import { isHr } from '@/lib/roles';
import { errorMessage, trpc } from '@/lib/trpc';

const TABS = ['assignments', 'contracts', 'documents', 'onboarding', 'emails', 'profile'] as const;

export const Route = createFileRoute('/_app/employees/$employeeId')({
  validateSearch: z.object({ tab: z.enum(TABS).optional().catch(undefined) }),
  component: EmployeePage,
});

function EmployeePage() {
  const t = uk.personnel;
  const { employeeId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { me } = Route.useRouteContext();
  const canWrite = isHr(me.role);
  const query = useQuery(trpc.employees.get.queryOptions(employeeId));
  const [dismissing, setDismissing] = useState(false);
  const [account, setAccount] = useState(false);

  if (query.isPending) return <Skeleton className="h-64" />;
  if (query.isError) return <EmptyState title={errorMessage(query.error)} />;
  const e = query.data;
  const today = new Date().toISOString().slice(0, 10);
  const current = e.assignments.filter(
    (a) => a.startsAt <= today && (!a.endsAt || a.endsAt >= today),
  );
  const missing = e.missingDocuments.length;
  const onboardingLeft = e.onboarding.filter((x) => !x.doneAt).length;

  return (
    <>
      <div className="mb-4 space-y-3">
        <Link
          to="/employees"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-4" /> {t.employees}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold tracking-tight">
              {fullName(e)} <EmployeeStatusBadge status={e.status} />
            </h1>
            <p className="text-sm text-muted-foreground">
              {current
                .map((a) => `${a.positionTitle} · ${a.projectCode} · ${a.fte} FTE`)
                .join('; ') || t.noAssignments}
            </p>
            <p className="text-sm text-muted-foreground">
              {[
                e.phoneE164 ? formatPhone(e.phoneE164) : e.phoneRaw,
                e.email,
                e.hiredAt && `${t.hiredAt}: ${formatDate(e.hiredAt)}`,
                e.dismissedAt && `${t.dismissedAt}: ${formatDate(e.dismissedAt)}`,
                e.accountEmail && `${t.account}: ${e.accountEmail}`,
              ]
                .filter(Boolean)
                .join('   ·   ')}
            </p>
          </div>
          {canWrite && (
            <div className="flex flex-wrap gap-2">
              {!e.userId && e.status === 'active' && (
                <Button variant="outline" onClick={() => setAccount(true)}>
                  <KeyRoundIcon /> {t.createAccount}
                </Button>
              )}
              {e.status === 'active' && (
                <Button variant="outline" onClick={() => setDismissing(true)}>
                  <UserRoundXIcon /> {t.dismiss}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <Tabs
        value={search.tab ?? 'assignments'}
        onValueChange={(v) =>
          void navigate({
            search: { tab: v === 'assignments' ? undefined : (v as (typeof TABS)[number]) },
            replace: true,
          })
        }
      >
        <TabsList>
          <TabsTrigger value="assignments">{t.tabAssignments}</TabsTrigger>
          <TabsTrigger value="contracts">{t.tabContracts}</TabsTrigger>
          <TabsTrigger value="documents">
            {t.tabDocuments}
            {missing > 0 && <span className="ml-1 text-amber-600">{missing}</span>}
          </TabsTrigger>
          <TabsTrigger value="onboarding">
            {t.tabOnboarding}
            {onboardingLeft > 0 && (
              <span className="ml-1 text-muted-foreground">{onboardingLeft}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="emails">{uk.mailbox.tab}</TabsTrigger>
          <TabsTrigger value="profile">{t.tabProfile}</TabsTrigger>
        </TabsList>
        <TabsContent value="assignments" className="pt-2">
          <AssignmentsTab employee={e} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="contracts" className="pt-2">
          <ContractsTab employee={e} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="documents" className="pt-2">
          <DocumentsTab employee={e} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="onboarding" className="pt-2">
          <OnboardingTab employee={e} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="emails" className="max-w-3xl space-y-6 pt-2">
          {canWrite && (
            <section className="space-y-2">
              <h3 className="font-medium">{uk.mailbox.title}</h3>
              {e.userId ? (
                <MailboxPanel userId={e.userId} lastName={e.lastName} firstName={e.firstName} />
              ) : (
                <p className="text-sm text-muted-foreground">{uk.mailbox.needAccount}</p>
              )}
            </section>
          )}
          {canWrite && (
            <SendEmailButton
              target={{ employeeId: e.id }}
              context="employee"
              defaultEmail={e.email ?? e.accountEmail}
            />
          )}
          <EmailHistory target={{ employeeId: e.id }} />
          <Correspondence email={e.email ?? e.accountEmail} />
        </TabsContent>
        <TabsContent value="profile" className="max-w-3xl pt-2">
          <EmployeeForm key={e.updatedAt.getTime()} employee={e} disabled={!canWrite} />
        </TabsContent>
      </Tabs>

      <DismissDialog employee={e} open={dismissing} onOpenChange={setDismissing} />
      {account && <AccountDialog employee={e} open={account} onOpenChange={setAccount} />}
    </>
  );
}
